import React from 'react';
import {
  Box, HStack, IconButton, Input, Select, Text, useColorModeValue,
  Table, Thead, Tbody, Tr, Th, Td,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import useItems from '../../hooks/useItems';
import { RnwyJobMisc } from './rnwyVenues';
import { money } from './rnwyCfg';

// item 654 — "One-time add-ons" (was item 653 "Add special services"): the ONE consistent way to
// add one-off, one-time line items to an RNWY quote — the FULL Items engine, picked from the real
// Items catalog (SKU + price auto-fill, grouped Hardware / Services) or typed as a one-off. Each
// row's LINE TOTAL (quantity × unit) is stored in `amount`; the total rolls into a single ONE-TIME
// number. Reused in two places: PROPOSAL-level (rolls onto all three options) and SwitchPilot (rolls
// onto the SwitchPilot one-time config) — pass title/hint/subtotalLabel to word each for its context.

const round2 = (n: number): number => Math.round(n * 100) / 100;
export const rnwyAddOnLineTotal = (r: RnwyJobMisc): number => round2((r.quantity ?? 1) * (r.unitAmount ?? r.amount ?? 0));
export const rnwyAddOnsTotal = (rows: RnwyJobMisc[]): number => round2((rows ?? []).reduce((s, r) => s + rnwyAddOnLineTotal(r), 0));
const lineTotal = rnwyAddOnLineTotal;

interface Props {
  value: RnwyJobMisc[];
  onChange: (rows: RnwyJobMisc[]) => void;
  title?: string;
  hint?: string;
  subtotalLabel?: string;
}

const RnwySpecialServices: React.FC<Props> = ({
  value, onChange, title, hint, subtotalLabel,
}) => {
  const { data } = useItems();
  const catalog = (data?.index ?? []).filter((i) => !i.isArchive);
  const services = catalog.filter((i) => !i.isHardware);
  const hardware = catalog.filter((i) => i.isHardware);

  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  const strong = useColorModeValue('gray.800', 'whiteAlpha.900');
  // Chad (2026-07-14) — in the AUTOMATED builder a catalog add shows only SKU, description
  // and GRP price; the suggested markup is applied once at the whole-quote level (Step 4
  // Pricing), NOT per add-on. So no per-row suggested-resale figure here.
  const skuColor = useColorModeValue('gray.600', 'whiteAlpha.700');

  const setRow = (i: number, patch: Partial<RnwyJobMisc>): void => {
    onChange(value.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, ...patch };
      next.amount = lineTotal(next);
      return next;
    }));
  };
  const removeRow = (i: number): void => onChange(value.filter((_, idx) => idx !== i));
  const addOneOff = (): void => onChange([...value, {
    label: '', amount: 0, quantity: 1, unitAmount: 0,
  }]);
  const addFromCatalog = (id: string): void => {
    const it = catalog.find((x) => x.id === id);
    if (!it) return;
    // item 674 — the item's GROUP PRICE is the directly-entered `retailEach` (the "Group Price"
    // column in Items — NOT cost × markup; there is no cost basis). Use it as-is for hardware AND
    // services; the user can still override the unit per row.
    const unit = round2(it.retailEach ?? 0);
    onChange([...value, {
      // item 779b (Chad) — the quote printed "3 × RNWY" when the catalog item's brand and
      // description are the same word (or the description is blank): prefer the DESCRIPTION
      // as the printed name and only prefix the brand when it adds information.
      // item 991 (Chad) — a description-less item printed brand ALONE ("RNWY", nothing else):
      // a bare brand identifies nothing, so fall to brand — SKU before brand-only.
      label: (() => {
        const brand = (it.brand || '').trim();
        const desc = (it.description || '').trim();
        const sku = (it.sku || '').trim();
        if (desc && brand && brand.toLowerCase() !== desc.toLowerCase()) return `${brand} — ${desc}`;
        if (desc) return desc;
        if (brand && sku) return `${brand} — ${sku}`;
        return sku || brand || 'Service';
      })(),
      sku: it.sku || undefined,
      // dart (2026-07-14) — keep the catalog id so the materialized invoice line resolves this
      // SKU's commission reasons (was dropped, so add-ons earned nothing).
      itemId: it.id || undefined,
      quantity: 1,
      unitAmount: unit,
      amount: unit,
    }]);
  };

  const subtotal = round2(value.reduce((s, r) => s + lineTotal(r), 0));

  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <Text fontSize="xs" fontWeight="semibold">{title}</Text>
        <HStack spacing={2}>
          {/* pick from the real Items catalog (grouped Hardware / Services) — resets after add */}
          <Select
            size="xs"
            w="200px"
            placeholder="Add from catalog…"
            value=""
            aria-label="Add special service from catalog"
            onChange={(e) => { if (e.target.value) addFromCatalog(e.target.value); }}
          >
            {services.length > 0 && (
              <optgroup label="Services">
                {services.map((i) => (
                  <option key={i.id} value={i.id}>{`${i.brand || i.sku || 'Service'}${i.sku ? ` · ${i.sku}` : ''}`}</option>
                ))}
              </optgroup>
            )}
            {hardware.length > 0 && (
              <optgroup label="Hardware">
                {hardware.map((i) => (
                  <option key={i.id} value={i.id}>{`${i.brand || i.sku || 'Hardware'}${i.sku ? ` · ${i.sku}` : ''}`}</option>
                ))}
              </optgroup>
            )}
          </Select>
          <IconButton size="xs" aria-label="Add one-off special service" icon={<FiPlus />} onClick={addOneOff} title="Add a one-off line" />
        </HStack>
      </HStack>
      {value.length === 0 ? (
        <Text fontSize="xx-small" color={sub} mb={1}>{hint}</Text>
      ) : (
        <Box overflowX="auto">
          {/* Chad (2026-07-14) — the standardized quote-line layout: aligned columns under real
              headers (Grp Price says grp, so the per-row "grp $" prefix is gone) instead of the
              cluttered floating labels. */}
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Item</Th>
                <Th w="130px">Item #</Th>
                <Th w="92px" isNumeric>Qty</Th>
                <Th w="120px" isNumeric>Grp Price</Th>
                <Th w="110px" isNumeric>Amount</Th>
                <Th w="1%" />
              </Tr>
            </Thead>
            <Tbody>
              {value.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Tr key={`svc-${i}`}>
                  <Td>
                    <Input
                      size="sm"
                      placeholder="Service / description"
                      aria-label="special service label"
                      value={r.label}
                      onChange={(e) => setRow(i, { label: e.target.value })}
                    />
                  </Td>
                  {/* item 668 — full item number (SKU) */}
                  <Td>
                    <Text fontSize="xs" color={skuColor} isTruncated title={r.sku ?? ''}>{r.sku ?? '—'}</Text>
                  </Td>
                  <Td isNumeric>
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      step="1"
                      textAlign="right"
                      aria-label="special service quantity"
                      value={r.quantity ?? 1}
                      onChange={(e) => setRow(i, { quantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                    />
                  </Td>
                  <Td isNumeric>
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      step="0.01"
                      textAlign="right"
                      placeholder="0.00"
                      aria-label="special service unit amount"
                      value={r.unitAmount || r.amount || ''}
                      onChange={(e) => setRow(i, { unitAmount: e.target.value === '' ? 0 : Number(e.target.value) })}
                    />
                  </Td>
                  <Td isNumeric>
                    <Text fontSize="sm" color={strong}>{money(lineTotal(r))}</Text>
                  </Td>
                  <Td>
                    <IconButton size="xs" variant="ghost" aria-label="Remove special service" icon={<FiTrash2 />} onClick={() => removeRow(i)} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <HStack justify="flex-end" mt={2} spacing={4} pr={10}>
            <Text fontSize="xs" color={sub}>{subtotalLabel}</Text>
            <Text fontSize="sm" fontWeight="bold" color={strong} minW="110px" textAlign="right">{money(subtotal)}</Text>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

RnwySpecialServices.defaultProps = {
  title: 'One-time add-ons',
  hint: 'Extra one-off services or hardware for this job — pick from your Items (SKU + price fill in) or add a one-off. The total is added to the one-time price on every option.',
  subtotalLabel: 'Add-ons subtotal (added to one-time)',
};

export default RnwySpecialServices;
